import GL from "_src/engine/gl";
import extensions from "_src/engine/extensions";
import { stencilValue, drawQueryState } from "_src/engine/constants";
import config from "_src/config";

let _gl = null;
let _instanceExt = null;

let _runningTimeQuery = null;
let _awaitingTimeQuery = null;

let attachGeometry = null;
let detachGeometry = null;
let attachInstancedGeometry = null;
let detachInstancedGeometry = null;

export default {

  init() {
    _gl = GL.getContext();
    _instanceExt = extensions.get("ANGLE_instanced_arrays");
    initGeometrySetters();
    prepareDrawing();
  },

  cleanup() {
    _gl = null;
    _instanceExt = null;
    attachGeometry = null;
    detachGeometry = null;
    attachInstancedGeometry = null;
    detachInstancedGeometry = null;
  },

  startTimeQuery() {
    if (_awaitingTimeQuery) {
      return;
    }

    const ext = extensions.get("EXT_disjoint_timer_query_webgl2");
    _runningTimeQuery = _gl.createQuery();
    _gl.beginQuery(ext.TIME_ELAPSED_EXT, _runningTimeQuery);
  },

  stopTimeQuery() {
    if (!_runningTimeQuery) {
      return;
    }
    const ext = extensions.get("EXT_disjoint_timer_query_webgl2");
    _gl.endQuery(ext.TIME_ELAPSED_EXT);
    _awaitingTimeQuery = _runningTimeQuery;
    _runningTimeQuery = null;
  },

  getTimeQueryResult() {
    if (!_awaitingTimeQuery) {
      return;
    }

    let elapsedNanos = null;
    const available = _gl.getQueryParameter(_awaitingTimeQuery, _gl.QUERY_RESULT_AVAILABLE);
    if (available) {
      elapsedNanos = _gl.getQueryParameter(_awaitingTimeQuery, _gl.QUERY_RESULT);
      _awaitingTimeQuery = null;
    }

    return elapsedNanos;
  },

  renderScene(scene, skipChunkTests = false) {
    const camera = scene.camera;

    if (!camera) {
      return;
    }

    scene.updateTransforms();

    const framebuffer = scene.getFramebuffer();

    framebuffer.bind(_gl);

    let clearMask = 0;

    if (scene.clearedColor) {

      const cc = scene.clearColor;
      _gl.clearColor(cc[0], cc[1], cc[2], cc[3]);

      clearMask |= _gl.COLOR_BUFFER_BIT;

      _gl.colorMask(true, true, true, true);
    }

    if (scene.clearedDepth) {
      clearMask |= _gl.DEPTH_BUFFER_BIT;
    }

    if (scene.clearedStencil) {
      clearMask |= _gl.STENCIL_BUFFER_BIT;
    }

    if (clearMask) {
      _gl.clear(clearMask);
    }

    _gl.viewport(camera.viewport[0], camera.viewport[1],
                 camera.viewport[2], camera.viewport[3]);

    if (scene.batcher.needsSort) {
      scene.batcher.sortObjects(camera.trans);
    }

    scene.batcher.generatePasses();

    _gl.blendEquationSeparate(_gl.FUNC_ADD, _gl.FUNC_ADD);

    // OPAQUE
    const opaqueQueue = scene.batcher.getOpaqueQueue();

    let shaderID = 0;
    let sharedShader = null;
    while (opaqueQueue.length) {

      const object = opaqueQueue.pop();
      const shader = object.shader;

      if (shaderID !== shader.id) {
        setupShader(shader, scene);
        shaderID = shader.id;
        sharedShader = shader;
      }

      const camera = scene.camera;

      drawMesh(object, sharedShader, camera, skipChunkTests);

      shader.renderCallback();

    }

    // BLEND
    if (scene.batcher.sortBlend) {
      scene.batcher.sortBlendQueue(camera.trans);
    }

    const blendQueue = scene.batcher.getBlendQueue();
    const blendingForbidden = scene.isDepthPrepass || scene.isPostBlending;
    if (!blendingForbidden) {
      _gl.enable(_gl.BLEND);
    }
    _gl.depthMask(blendingForbidden);

    shaderID = 0;
    sharedShader = null;
    while (blendQueue.length) {

      const object = blendQueue.pop();
      const shader = object.shader;

      if (shaderID !== shader.id) {
        setupShader(shader, scene);
        shaderID = shader.id;
        sharedShader = shader;
      }

      const camera = scene.camera;

      drawMesh(object, sharedShader, camera, skipChunkTests);

      shader.renderCallback();

    }

    _gl.disable(_gl.BLEND);
    _gl.depthMask(true);

    const resolveFramebuffer = scene.getResolvedBuffer();

    if (resolveFramebuffer) {
      drawResolve(framebuffer, resolveFramebuffer);
    }

    if (scene.needsReadPixels) {
      scene.readPixels();
    }

  }

}

function setupShader(shader, scene) {

  shader.updateSceneDirectives(scene);

  if (shader.needsUpdate) {
    shader.compile();
  }

  // TODO: implement better solution
  if (shader.transparent) {
    _gl.blendFunc(shader.sourceBlendFactor, shader.destBlendFactor);
    _gl.blendEquationSeparate(shader.blendEquationRgb, shader.blendEquationAlpha);
  }

  if (shader.drawColor) {
    _gl.colorMask(true, true, true, true);
  } else {
    _gl.colorMask(false, false, false, false);
  }

  if (shader.stencilTest) {
    _gl.enable(_gl.STENCIL_TEST);
    _gl.stencilFunc(shader.stencilFunc, shader.stencilReferenceVal, shader.stencilTestMask);
    _gl.stencilOpSeparate(shader.frontFace, shader.stencilFrontFail, shader.stencilFrontZFail, shader.stencilFrontZPass);
    _gl.stencilOpSeparate(shader.backFace, shader.stencilBackFail, shader.stencilBackZFail, shader.stencilBackZPass);

    _gl.stencilMask(shader.stencilUpdateMask);
  } else {
    _gl.disable(_gl.STENCIL_TEST);
  }

  if (shader.depthTest) {
    _gl.enable(_gl.DEPTH_TEST);
  } else {
    _gl.disable(_gl.DEPTH_TEST);
  }

  if (shader.scissorTest) {
    _gl.enable(_gl.SCISSOR_TEST);
    _gl.scissor(...shader.scissor);
  } else {
    _gl.disable(_gl.SCISSOR_TEST);
  }

  if (shader.backFaceCulling) {
    _gl.enable(_gl.CULL_FACE);
  } else {
    _gl.disable(_gl.CULL_FACE);
  }

  if (shader.polygonOffsetEnabled) {
    _gl.enable(_gl.POLYGON_OFFSET_FILL);
    _gl.polygonOffset(...shader.polygonOffset);
  } else {
    _gl.disable(_gl.POLYGON_OFFSET_FILL);
  }

  _gl.useProgram(shader.program);

  _gl.frontFace(shader.cullingFrontFace);

  shader.updateSceneUniformValues(scene);
  setupUniforms(shader.sceneUniformsSetters);
  setupUniforms(shader.shaderUniformsSetters);

}

function drawMesh(obj, shader, camera, skipChunkTests) {

  const chunks = obj.chunks;

  if (chunks.length == 0) {
    return;
  }

  shader.updateObjectUniformValues(obj);
  setupUniforms(shader.objectUniformsSetters);

  obj.setupTextures();
  shader.setupTextures();

  for (let i = 0; i < chunks.length; ++i) {

    const chunk = chunks[i];

    if (!chunk.hasDrawElements() && chunk.isEmpty()) {
      continue;
    }

    if (!skipChunkTests && !obj.getChunkQueryResult(chunk)) {
      continue;
    }

    const isInFrustum = obj.chunkFrustumTest(chunk, camera.frustum);

    if (!skipChunkTests && !isInFrustum) {
      continue;
    }

    if (!chunk.updateGlBuffers(shader)) {
      continue;
    }

    let importedChunksUpdated = true;
    for (const importedModel of obj.importedModelsForInstancing) {
      for (const importedChunk of importedModel.chunks) {
        //Note: don't try to update others if one failed
        importedChunksUpdated = importedChunksUpdated && importedChunk.updateGlBuffers(shader);
      }
    }
    if (!importedChunksUpdated) {
      continue;
    }

    obj.updateAttributes(shader, chunk);

    if (config.drawQueries) {
      switch (obj.getChunkQueryState(chunk)) {
        case drawQueryState.SHOULD_RUN:
          if (!skipChunkTests) {
            _gl.beginQuery(_gl.ANY_SAMPLES_PASSED, obj.getChunkDrawQuery(chunk));
          }
          break;
        case drawQueryState.IN_PROGRESS:
          if (_gl.getQueryParameter(obj.getChunkDrawQuery(chunk), _gl.QUERY_RESULT_AVAILABLE)) {
            const result = _gl.getQueryParameter(obj.getChunkDrawQuery(chunk), _gl.QUERY_RESULT);
            const testPassed = result == 1;
            obj.setChunkQueryResult(chunk, testPassed);
            obj.setChunkQueryState(chunk, drawQueryState.FINISHED);
          }
          break;
      }
    }

    shader.updateChunkUniformValues(chunk);
    setupUniforms(shader.chunkUniformsSetters);

    const instancedAttributes = obj.getInstancedAttributes(chunk);
    if (instancedAttributes.size > 0 && _instanceExt) {

      for (const [modelOwner, attributesMapsForModel] of instancedAttributes.entries()) {
        shader.updateObjectUniformValues(modelOwner);
        setupUniforms(shader.objectUniformsSetters);

        for (const attributesObject of attributesMapsForModel) {
          const instanceCount = attributesObject.instancesCount;

          attachInstancedGeometry(obj, chunk, attributesObject, shader.instanceDivisor);
          chunk.drawInstanced(_instanceExt, instanceCount);
          detachInstancedGeometry(obj, chunk, attributesObject);
        }
      }

    } else {
      attachGeometry(obj, chunk);
      chunk.draw(_gl);
      detachGeometry(obj, chunk);
    }

    if (config.drawQueries && !skipChunkTests && obj.getChunkQueryState(chunk) === drawQueryState.SHOULD_RUN) {
      _gl.endQuery(_gl.ANY_SAMPLES_PASSED);
      obj.setChunkQueryState(chunk, drawQueryState.IN_PROGRESS);
    }

  }

}

function drawResolve(readFrameBuffer, drawFrameBuffer) {

  const [width, height] = readFrameBuffer.size();

  _gl.bindFramebuffer(_gl.READ_FRAMEBUFFER, readFrameBuffer.fbo);
  _gl.bindFramebuffer(_gl.DRAW_FRAMEBUFFER, drawFrameBuffer.fbo);

  let blit_mask = 0;

  blit_mask |= _gl.COLOR_BUFFER_BIT;
  blit_mask |= _gl.DEPTH_BUFFER_BIT;

  _gl.blitFramebuffer(
      0, 0, width, height,
      0, 0, width, height,
      blit_mask, _gl.NEAREST
  );

}

function prepareDrawing() {

  _gl.clearColor(0.839, 0.839, 0.839, 1.0);
  _gl.clearDepth(1.0);
  _gl.clearStencil(stencilValue.CLEAR);
  _gl.enable(_gl.DEPTH_TEST);
  _gl.depthFunc(_gl.LEQUAL);

  _gl.colorMask(true, true, true, true);
  _gl.depthMask(true);

  _gl.enable(_gl.CULL_FACE);
  _gl.disable(_gl.POLYGON_OFFSET_FILL);
  _gl.disable(_gl.BLEND);
  _gl.frontFace(_gl.CCW);
  _gl.cullFace(_gl.BACK);

  _gl.clear(_gl.DEPTH_BUFFER_BIT | _gl.COLOR_BUFFER_BIT | _gl.STENCIL_BUFFER_BIT);

}

function setupUniforms(uniSetters) {
  for (var i = 0; i < uniSetters.length; i++) {
    uniSetters[i].commit();
  }
}

function initGeometrySetters() {

  attachGeometry = (function() {

    const VAOExtension = extensions.get("OES_vertex_array_object");

    if (VAOExtension) {
      return function(model, chunk) {
        const vao = model.getVao(chunk);
        VAOExtension.bindVertexArray(vao);
      }
    } else {
      return function(model, chunk) {
        const attributesMap = model.getAttributesMap(chunk);
        chunk.attachBuffers(_gl, attributesMap);
      }
    }

  })();

  detachGeometry = (function() {

    const VAOExtension = extensions.get("OES_vertex_array_object");

    if (VAOExtension) {
      return function() {
        VAOExtension.bindVertexArray(null);
      }
    } else {
      return function(obj, chunk) {

        const chunkAttributesMap = obj.getAttributesMap(chunk);
        chunk.detachBuffers(_gl, chunkAttributesMap);

      }
    }

  })();

  attachInstancedGeometry = (function() {

    const VAOExtension = extensions.get("OES_vertex_array_object");

    if (VAOExtension) {
      return function(model, chunk, attributesObject) {
        const vao = attributesObject.vao;
        VAOExtension.bindVertexArray(vao);
      }
    } else {
      return function(model, chunk, attributesObject, instanceDivisor) {
        const attributesMap = model.getAttributesMap(chunk);
        chunk.attachBuffers(_gl, attributesMap);

        const importedAttributesMap = attributesObject.attributesMap;
        chunk.attachInstancedBuffers(_gl, _instanceExt, importedAttributesMap, instanceDivisor);
      }
    }

  })();

  detachInstancedGeometry = (function() {

    const VAOExtension = extensions.get("OES_vertex_array_object");

    if (VAOExtension) {
      return function() {
        VAOExtension.bindVertexArray(null);
      }
    } else {
      return function(model, chunk, attributesObject) {
        const chunkAttributesMap = model.getAttributesMap(chunk);
        chunk.detachBuffers(_gl, chunkAttributesMap);

        const instancedAttributesMap = attributesObject.attributesMap;
        chunk.detachInstancedBuffers(_gl, _instanceExt, instancedAttributesMap);

      }
    }

  })();

}
